import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import PropTypes from 'prop-types'
import get from 'lodash.get'
import { useFormattingContext } from '../../../FormattingProvider/FormattingContext'
import ComponentLine from '../../../../charts/echarts/ComponentLine'
import { useChartContext } from '../../../../charts/echarts/ChartProvider'
import { usePipelineDataContextWithVariables } from '../../provider/DDPDataProvider'

const useFormatters = (valueFormat, categoryFormat) => {
  const { formatter } = useFormattingContext()

  const valueFormatter = useCallback(d => formatter(d, valueFormat), [formatter, valueFormat])
  const categoryFormatter = useCallback(d => formatter(d, categoryFormat), [formatter, categoryFormat])

  return {
    valueFormatter,
    categoryFormatter
  }
}

function PipelineComponentMultiLine ({
  pipeline,
  variables,
  dataAccessor,
  seriesAccessor,
  nameAccessor,
  xAccessor,
  yAccessor,
  valueFormat,
  categoryFormat,
  ...seriesProps
}) {
  const { valueFormatter, categoryFormatter } = useFormatters(valueFormat, categoryFormat)
  const { setSeriesLoading, removeSeries } = useChartContext()
  const { data, isLoading } = usePipelineDataContextWithVariables(pipeline, variables)
  const chartSeries = useMemo(() => {
    const seriesCollection = get(data, dataAccessor)
    return (seriesCollection || []).map(seriesItem => {
      const name = get(seriesItem, nameAccessor)
      const seriesData = get(seriesItem, seriesAccessor)
      return {
        name,
        series: (seriesData || []).map(item => [get(item, xAccessor), get(item, yAccessor)])
      }
    })
  }, [data, seriesAccessor, xAccessor, yAccessor, dataAccessor, nameAccessor])

  // Track current and previous series names to clean up stale ones
  const currentSeriesNames = useMemo(
    () => chartSeries.map((series) => series.name),
    [chartSeries]
  )
  const previousSeriesNamesRef = useRef([])

  useEffect(() => {
    setSeriesLoading(seriesProps.seriesId, isLoading)
  }, [seriesProps.seriesId, isLoading, setSeriesLoading])

  useEffect(() => {
    if (!isLoading) {
      const previousNames = previousSeriesNamesRef.current
      const currentNames = new Set(currentSeriesNames)

      const staleSeries = previousNames.filter(name => !currentNames.has(name))
      staleSeries.forEach((seriesName) => removeSeries(seriesName))
      previousSeriesNamesRef.current = currentSeriesNames
    }
  }, [currentSeriesNames, isLoading, removeSeries])

  if (isLoading) return null

  return (
    <>
      {(chartSeries || []).map(series => (
        <ComponentLine
          key={series.name}
          name={series.name}
          data={series?.series || []}
          valueFormat={valueFormatter}
          categoryFormat={categoryFormatter}
          {...seriesProps}
          seriesId={series.name}
        />
      ))}
    </>
  )
}

PipelineComponentMultiLine.propTypes = {
  /** The data pipeline to execute */
  pipeline: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
  variables: PropTypes.object,
  dataAccessor: PropTypes.string,
  seriesAccessor: PropTypes.string,
  nameAccessor: PropTypes.string,
  xAccessor: PropTypes.string,
  yAccessor: PropTypes.string,
  valueFormat: PropTypes.string,
  categoryFormat: PropTypes.string
}

PipelineComponentMultiLine.defaultProps = {
  valueFormat: 'marketValue',
  categoryFormat: 'date',
  height: 300,
  dataAccessor: 'data',
  nameAccessor: 'levelName'
}

export default PipelineComponentMultiLine
